/**
 * CitationFooter — numbered source cards shown below Lib Chat assistant messages.
 *
 * Two modes:
 * 1. Citation mode (citationNumbers provided): shows only cited sources with [n] badges
 * 2. Fallback mode (no citations): groups sources by article, shows article links
 *    (backward compatible with pre-citation responses)
 */

import { Link } from 'react-router-dom';
import { Highlighter, MessageSquare } from 'lucide-react';
import { ICON_SIZE } from '../../design-system';

const SOURCE_TYPE_ICON = {
  highlight: Highlighter,
  comment: MessageSquare,
};

const CitationFooter = ({ sources, citationNumbers, label }) => {
  if (!sources?.length) return null;

  const hasCitations = citationNumbers?.length > 0;

  // Citation mode: show only cited sources with number badges
  if (hasCitations) {
    const sourceMap = new Map();
    for (const s of sources) {
      if (s.source_number != null) sourceMap.set(s.source_number, s);
    }

    const citedSources = citationNumbers
      .map(n => ({ number: n, source: sourceMap.get(n) }))
      .filter(({ source }) => source != null);

    if (!citedSources.length) return null;

    return (
      <div className="citation-footer">
        {label && <div className="citation-footer-label">{label}</div>}
        <div className="citation-footer-cards">
          {citedSources.map(({ number, source }) => {
            const Icon = SOURCE_TYPE_ICON[source.source_type];

            return (
              <Link
                key={number}
                to={`/article/${source.article_id}`}
                className="citation-card"
              >
                <span className="citation-card-number">{number}</span>
                {Icon && <Icon size={ICON_SIZE.xxs} className="citation-card-icon" />}
                <span className="citation-card-title">
                  {source.article_title || 'Untitled'}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  // Fallback mode: group by article, show article links (pre-citation compat)
  const merged = new Map();
  for (const source of sources) {
    const key = source.article_id || `title:${source.article_title}`;
    if (!merged.has(key)) {
      merged.set(key, { ...source, __count: 1 });
    } else {
      merged.get(key).__count += 1;
    }
  }
  const mergedSources = Array.from(merged.values());

  return (
    <div className="rag-sources">
      {label && <div className="rag-sources-label">{label}</div>}
      {mergedSources.map((source, i) => (
        <Link
          key={`${source.article_id || source.article_title || 'source'}-${i}`}
          to={`/article/${source.article_id}`}
          className="rag-source-link"
        >
          <span>{source.article_title || 'Untitled'}</span>
          {source.__count > 1 && (
            <span className="rag-source-count-badge">{source.__count}</span>
          )}
        </Link>
      ))}
    </div>
  );
};

export default CitationFooter;
